import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import AppMenu from "../components/layout/AppMenu";
import ResizableSplit from "../components/layout/ResizableSplit";
import Spinner from "../components/layout/Spinner";
import {
    MatchesPanel,
    PlaylistsPanel,
    ReportsPanel,
} from "../components/layout/TeamPlayerPanels";
import { usingFirestore } from "../components/utils/firestoreData";
import {
    fetchMyMatches,
    matchesForTeamName,
} from "../components/utils/teamPlayerData";
import {
    createTeam,
    fetchAllPlayers,
    fetchAllTeams,
    updateTeam,
} from "../components/utils/teamsPlayers";
import { classNames } from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";

function makeDate(seconds) {
  if (!seconds) return "";
  try {
    return new Date(seconds * 1000).toLocaleDateString();
  } catch {
    return "";
  }
}

const blankPlayer = () => ({
  firstName: "",
  lastName: "",
  nickName: "",
  shirtNumber: "",
});

// A search box that lists existing players from the database and calls onPick
// when one is chosen. `isExcluded(player)` hides players already on the roster.
function PlayerSearch({ players, isExcluded, onPick, t }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const matches = term
    ? players
        .filter((p) => !isExcluded(p))
        .filter((p) =>
          `${p.firstName ?? ""} ${p.lastName ?? ""} ${p.nickName ?? ""}`
            .toLowerCase()
            .includes(term)
        )
        .slice(0, 8)
    : [];
  return (
    <div className="relative">
      <input
        type="text"
        className="input-generic"
        placeholder={t("Search existing players...")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {matches.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded border border-base-300 bg-base-100 shadow">
          {matches.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full px-2 py-1 text-left text-sm hover:bg-base-200"
                onClick={() => {
                  onPick(p);
                  setQ("");
                }}
              >
                {`${p.firstName ?? ""} ${p.lastName ?? ""}`.trim()}
                {p.nickName ? ` (${p.nickName})` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Case-insensitive name match between a roster row and an existing player.
function sameName(a, b) {
  return (
    `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase() ===
    `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase()
  );
}

function TeamsPage() {
  const { firebaseUser, checkingStatus } = useAuthStatus();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTeamId =
    location.state?.teamId ??
    sessionStorage.getItem("vblive-teams-selectedId") ??
    null;

  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [selectedId, setSelectedId] = useState(initialTeamId);
  const [search, setSearch] = useState("");
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", players: [] });
  const [editingTeam, setEditingTeam] = useState(null);
  const [saving, setSaving] = useState(false);
  const selectedRef = useRef(null);
  // When set, the shirt-number input of the last roster row is focused on the
  // next render (after the New team / clone button or Enter adds a row).
  const focusNewRowRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [ts, ms] = await Promise.all([
          fetchAllTeams(),
          fetchMyMatches(),
        ]);
        if (cancelled) return;
        setTeams(ts);
        setAllMatches(ms);
        setSelectedId((prev) => prev ?? ts[0]?.id ?? null);
        fetchAllPlayers()
          .then((ps) => {
            if (!cancelled) setAllPlayers(ps);
          })
          .catch((e) => console.log("Failed to load players", e));
      } catch (e) {
        console.log("Failed to load teams", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  const term = search.trim().toLowerCase();
  const filteredTeams = term
    ? teams.filter((tm) => String(tm.name ?? "").toLowerCase().includes(term))
    : teams;

  const selectedTeam = useMemo(
    () => teams.find((tm) => tm.id === selectedId) ?? null,
    [teams, selectedId]
  );

  const teamMatches = useMemo(
    () => (selectedTeam ? matchesForTeamName(allMatches, selectedTeam.name) : []),
    [allMatches, selectedTeam]
  );

  const nameCounts = useMemo(
    () =>
      teams.reduce((acc, tm) => {
        const k = String(tm.name ?? "").toLowerCase();
        acc[k] = (acc[k] ?? 0) + 1;
        return acc;
      }, {}),
    [teams]
  );

  const openPlayer = (player) => {
    if (!player?.playerId) return;
    navigate("/players", { state: { playerId: player.playerId } });
  };

  // Scroll the selected team into view (e.g. when arriving from the Players
  // page via a team badge).
  useEffect(() => {
    if (!loading && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [selectedId, loading]);

  // Persist selection so it survives navigating away and back.
  useEffect(() => {
    if (selectedId) sessionStorage.setItem("vblive-teams-selectedId", selectedId);
  }, [selectedId]);

  const reloadTeams = async () => {
    const [ts, ps] = await Promise.all([fetchAllTeams(), fetchAllPlayers()]);
    setTeams(ts);
    setAllPlayers(ps);
    return ts;
  };

  const setRosterRow = (idx, key, value) =>
    setTeamForm((prev) => {
      const players = prev.players.map((row, i) =>
        i === idx ? { ...row, [key]: value } : row
      );
      return { ...prev, players };
    });

  const addRosterRow = () => {
    focusNewRowRef.current = true;
    setTeamForm((prev) => ({
      ...prev,
      players: [...prev.players, blankPlayer()],
    }));
  };

  // Pre-fill the New team modal with an existing team's roster so the user can
  // tweak the name and roster before saving it as a brand new team.
  const cloneTeam = () => {
    if (!selectedTeam) return;
    setEditingTeam(null);
    setTeamForm({
      name: `${selectedTeam.name} (copy)`,
      players: (selectedTeam.players ?? []).map((p) => ({
        firstName: p.firstName ?? "",
        lastName: p.lastName ?? "",
        nickName: p.nickName ?? "",
        shirtNumber: p.shirtNumber ?? "",
      })),
    });
    setCreatingTeam(true);
  };

  // Open the team modal on the selected team to edit its name and full roster.
  const editTeam = () => {
    if (!selectedTeam) return;
    setEditingTeam(selectedTeam);
    setTeamForm({
      name: selectedTeam.name,
      players: (selectedTeam.players ?? []).map((p) => ({
        firstName: p.firstName ?? "",
        lastName: p.lastName ?? "",
        nickName: p.nickName ?? "",
        shirtNumber: p.shirtNumber ?? "",
      })),
    });
    setCreatingTeam(true);
  };

  const removeRosterRow = (idx) =>
    setTeamForm((prev) => ({
      ...prev,
      players: prev.players.filter((_, i) => i !== idx),
    }));

  const submitNewTeam = async () => {
    if (!teamForm.name.trim()) return;
    setSaving(true);
    try {
      const saved = editingTeam
        ? await updateTeam(editingTeam, teamForm)
        : await createTeam(teamForm);
      if (saved) {
        setSearch("");
        await reloadTeams();
        setSelectedId(saved.id);
        setCreatingTeam(false);
        setEditingTeam(null);
        setTeamForm({ name: "", players: [] });
      }
    } catch (e) {
      console.log("Failed to save team", e);
    } finally {
      setSaving(false);
    }
  };

  const teamList = (
    <div className="flex h-full flex-col border-r border-base-300">
      <div className="p-2">
        <input
          type="text"
          className="input-generic"
          placeholder={t("Search team...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {filteredTeams.length === 0 ? (
          <p className="p-3 text-sm text-base-content/60">{t("No teams yet.")}</p>
        ) : (
          <ul>
            {filteredTeams.map((tm) => (
              <li key={tm.id}>
                <button
                  ref={tm.id === selectedId ? selectedRef : null}
                  onClick={() => setSelectedId(tm.id)}
                  className={classNames(
                    "w-full border-b border-base-200 px-3 py-2 text-left",
                    tm.id === selectedId
                      ? "bg-primary/20"
                      : "hover:bg-base-200"
                  )}
                >
                  <div className="truncate text-sm font-medium">
                    {tm.name}
                    {nameCounts[String(tm.name ?? "").toLowerCase()] > 1 && (
                      <span className="ml-1 text-xs text-base-content/60">
                        ({t("roster")}: {tm.playerCount ?? 0})
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-base-content/60">
                    {(tm.playerCount ?? 0)} {t("players")} ·{" "}
                    {makeDate(tm.lastSeenSeconds)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const roster = (
    <div className="flex h-full flex-col border-r border-base-300">
      <div className="flex items-center justify-between border-b border-base-300 px-3 py-2">
        <h3 className="text-sm font-semibold">{t("Roster")}</h3>
        <div className="flex gap-1">
          <button
            className="btn btn-xs btn-ghost"
            onClick={cloneTeam}
            disabled={!selectedTeam}
            title={t("Create a new team from this roster")}
          >
            {t("Clone")}
          </button>
          <button
            className="btn btn-xs btn-ghost"
            onClick={editTeam}
            disabled={!selectedTeam}
          >
            {t("Edit team")}
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {!selectedTeam || (selectedTeam.players ?? []).length === 0 ? (
          <p className="p-2 text-sm text-base-content/60">
            {t("No players recorded.")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(selectedTeam.players ?? []).map((p, idx) => (
              <li key={idx}>
                <button
                  onClick={() => openPlayer(p)}
                  className="w-full rounded px-2 py-1 text-left text-sm hover:bg-base-200"
                >
                  {p.shirtNumber ? (
                    <span className="mr-1 text-base-content/60">
                      #{p.shirtNumber}
                    </span>
                  ) : null}
                  <span className="underline">
                    {`${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() ||
                      p.nickName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const detailPanels = (
    <div className="flex h-full min-w-0 flex-col gap-2 p-2">
      <MatchesPanel matches={teamMatches} t={t} />
      <PlaylistsPanel scope="team" refId={selectedTeam?.id} t={t} />
      <ReportsPanel
        scope="team"
        refId={selectedTeam?.id}
        matches={teamMatches}
        reportLabel={selectedTeam?.name ?? null}
        t={t}
      />
    </div>
  );

  const rightSide = selectedTeam ? (
    <ResizableSplit
      storageKey="vblive-teams-roster-w"
      defaultWidth={240}
      min={180}
      max={420}
      left={roster}
      right={detailPanels}
    />
  ) : (
    <div className="flex h-full items-center justify-center text-base-content/60">
      {t("Select a team.")}
    </div>
  );

  return (
    <div className="mt-2 flex h-[90vh] flex-col">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AppMenu />
          <h2 className="text-lg font-semibold">{t("Teams")}</h2>
        </div>
        <div className="flex gap-1">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              focusNewRowRef.current = true;
              setEditingTeam(null);
              setTeamForm({ name: "", players: [blankPlayer()] });
              setCreatingTeam(true);
            }}
            disabled={!usingFirestore()}
          >
            {t("New team")}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)}>
            {t("Back")}
          </button>
        </div>
      </div>
      {loading || checkingStatus ? (
        <Spinner />
      ) : !usingFirestore() ? (
        <div className="p-6 text-center text-base-content/60">
          {t(
            "Your teams and players will appear here after your data is migrated."
          )}
        </div>
      ) : (
        <div className="min-h-0 flex-1 rounded-md border border-base-300">
          <ResizableSplit
            storageKey="vblive-teams-list-w"
            defaultWidth={240}
            min={180}
            max={480}
            left={teamList}
            right={rightSide}
          />
        </div>
      )}

      {creatingTeam && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="mb-3 text-lg font-bold">
              {editingTeam ? t("Edit team") : t("New team")}
            </h3>
            <input
              type="text"
              className="input-generic mb-3"
              placeholder={t("Team name")}
              value={teamForm.name}
              onChange={(e) =>
                setTeamForm({ ...teamForm, name: e.target.value })
              }
            />
            <div className="mb-2">
              <PlayerSearch
                players={allPlayers}
                isExcluded={(p) =>
                  teamForm.players.some((row) => sameName(row, p))
                }
                onPick={(p) =>
                  setTeamForm((prev) => ({
                    ...prev,
                    players: [
                      ...prev.players,
                      {
                        firstName: p.firstName ?? "",
                        lastName: p.lastName ?? "",
                        nickName: p.nickName ?? "",
                        shirtNumber: "",
                      },
                    ],
                  }))
                }
                t={t}
              />
            </div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-base-content/60">
                {t("Roster")}
              </span>
              <button className="btn btn-xs" onClick={addRosterRow}>
                {t("Add player")}
              </button>
            </div>
            <div className="max-h-[45vh] overflow-y-auto">
              {teamForm.players.length === 0 ? (
                <p className="p-2 text-sm text-base-content/60">
                  {t("No players added yet.")}
                </p>
              ) : (
                teamForm.players.map((row, idx) => (
                  <div key={idx} className="mb-1 flex items-center gap-1">
                    <input
                      type="text"
                      className="input input-bordered input-xs w-12"
                      placeholder="#"
                      value={row.shirtNumber}
                      ref={(el) => {
                        if (
                          el &&
                          focusNewRowRef.current &&
                          idx === teamForm.players.length - 1
                        ) {
                          el.focus();
                          focusNewRowRef.current = false;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRosterRow();
                        }
                      }}
                      onChange={(e) =>
                        setRosterRow(idx, "shirtNumber", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      className="input input-bordered input-xs flex-1"
                      placeholder={t("First name")}
                      value={row.firstName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRosterRow();
                        }
                      }}
                      onChange={(e) =>
                        setRosterRow(idx, "firstName", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      className="input input-bordered input-xs flex-1"
                      placeholder={t("Last name")}
                      value={row.lastName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRosterRow();
                        }
                      }}
                      onChange={(e) =>
                        setRosterRow(idx, "lastName", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      className="input input-bordered input-xs flex-1"
                      placeholder={t("Nickname")}
                      value={row.nickName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRosterRow();
                        }
                      }}
                      onChange={(e) =>
                        setRosterRow(idx, "nickName", e.target.value)
                      }
                    />
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => removeRosterRow(idx)}
                      title={t("Remove")}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="modal-action">
              <button
                className="btn btn-sm"
                onClick={() => {
                  setCreatingTeam(false);
                  setEditingTeam(null);
                  setTeamForm({ name: "", players: [] });
                }}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={submitNewTeam}
                disabled={saving || !teamForm.name.trim()}
              >
                {saving ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : editingTeam ? (
                  t("Save")
                ) : (
                  t("Create")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamsPage;
